import { useMutation } from '@apollo/client/react';
import { ConfirmationDialog } from '@optools/components/dialog/confirmation-dialog';
import { TeamSpot } from '@optools/components/team-spot';
import { AlertDialogDescription } from '@optools/components/ui/alert-dialog';
import { Button } from '@optools/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@optools/components/ui/select';
import { usePolling } from '@optools/hooks/usePolling';
import { PROVISION_MEDIA } from '@optools/services/operator-gateway/btc/mutations';
import { BTC_GAME_PIPELINE } from '@optools/services/operator-gateway/btc/queries';
import {
  BtcScheduleContent,
  BtcScheduleGame,
  BtcSchedulePipeline,
} from '@optools/services/operator-gateway/btc/types';
import {
  MediaFeedType,
  PipelineStatus,
  Region,
} from '@optools/services/operator-gateway/types/generated/graphql';
import { useState } from 'react';
import { toast } from 'sonner';

const getTeamByFeedType = (game: BtcScheduleGame, feedType: MediaFeedType) => {
  if (feedType === MediaFeedType.HOME) {
    return game.home;
  }

  if (feedType === MediaFeedType.AWAY) {
    return game.away;
  }

  return null;
};

const getPipelineByFeedType = (pipelines: BtcSchedulePipeline[], feedType: MediaFeedType) => {
  return pipelines.find((pipeline) => pipeline.feedType === feedType);
};

const isRegionalPipelineProvisioned = (
  pipeline: BtcSchedulePipeline | undefined,
  region?: Region | undefined,
) => {
  // if Region is undefined then it is assumed that we are checking all the regions
  if (!region) {
    return (
      Object.values(Region).every((item) =>
        pipeline?.regionalPipelines?.map((pipe) => pipe.region).includes(item),
      ) &&
      pipeline?.regionalPipelines?.every(
        (item) =>
          item.status && ![PipelineStatus.ENDED, PipelineStatus.ARCHIVED].includes(item.status),
      )
    );
  } else {
    // If a region is defined then check that the region is correctly provisioned.
    const regionalPipelineStatus = pipeline?.regionalPipelines?.find(
      (pipeline) => pipeline.region === region,
    )?.status;
    return (
      !!regionalPipelineStatus &&
      ![PipelineStatus.ENDED, PipelineStatus.ARCHIVED].includes(regionalPipelineStatus)
    );
  }
};

export const ALL_REGIONS = 'ALL REGIONS';

export interface ProvisionPipelineButtonProps {
  content: BtcScheduleContent;
  game: BtcScheduleGame;
  pipelines: BtcSchedulePipeline[];
  scheduleDate: string;
}

export function ProvisionPipelineButton({
  content,
  game,
  pipelines,
  scheduleDate,
}: ProvisionPipelineButtonProps) {
  const [region, setRegion] = useState<Region | undefined>(undefined);

  const { callSign, feedType, mediaId } = content;

  const [provisionMedia, { loading }] = useMutation(PROVISION_MEDIA, {
    onCompleted: ({ provisionMedia }) => {
      const [{ mediaId, reason, success }] = provisionMedia;
      if (success) {
        toast.success('Provision Media Success', { description: `Media ID: ${mediaId}` });
        startPolling();
      } else {
        toast.error(`Provision Media Error`, {
          description: (
            <>
              <h2>Media ID: {mediaId}</h2>
              <p>{reason}</p>
            </>
          ),
          duration: Infinity,
        });
      }
    },
  });

  const { isPolling, startPolling } = usePolling({
    interval: 2000,
    onMaxAttemptsReached: () => {
      toast.warning('Failed to Fetch Provisioned Pipeline', {
        description:
          'Please refresh the page to ensure that the pipeline has been provisioned correctly.',
        duration: Infinity,
      });
    },
    query: BTC_GAME_PIPELINE,
    shouldStopPolling: ({ game }) => {
      if (!game?.pipelines) return;

      return game.pipelines.some((pipeline) => {
        const hasTargetMedia = pipeline.media.some((media) => media.id === mediaId);
        if (!hasTargetMedia) return false;

        return pipeline.regionalPipelines?.length > 0
          ? isRegionalPipelineProvisioned(pipeline, region)
          : pipeline.status !== PipelineStatus.ARCHIVED;
      });
    },
    variables: { gamePk: game.gamePk, scheduleDate },
  });

  const hasActivePipeline = () => {
    return isRegionalPipelineProvisioned(pipeline, region);
  };

  const team = getTeamByFeedType(game, feedType);
  const pipeline = getPipelineByFeedType(pipelines, feedType);
  const isLoading = isPolling || loading;

  // Note this is more percauntionary than anything if region remains unselected it will be undefined
  // and should not be included in the mutation, but this check elminates any chance of a region param being
  // passed during a fallback scenario.
  const handleOnConfirm = () => {
    provisionMedia({ variables: { mediaIds: [mediaId], region } });
  };

  return (
    <div className="flex w-full justify-start gap-2">
      <Select
        disabled={isLoading || (!!pipeline && isRegionalPipelineProvisioned(pipeline))}
        value={region ?? ALL_REGIONS}
        onValueChange={(value) => setRegion(value === ALL_REGIONS ? undefined : (value as Region))}>
        <SelectTrigger className="w-36">
          <SelectValue />
        </SelectTrigger>
        <SelectContent align="start">
          <SelectItem value={ALL_REGIONS}>{ALL_REGIONS}</SelectItem>
          <SelectItem value={Region.EAST}>{Region.EAST}</SelectItem>
          <SelectItem value={Region.CENTRAL}>{Region.CENTRAL}</SelectItem>
        </SelectContent>
      </Select>
      <ConfirmationDialog
        description={
          <AlertDialogDescription data-testid="pipeline-provision-confirmation-description">
            Are you sure you want to provision the{' '}
            <strong>
              {feedType} ({callSign})
            </strong>{' '}
            pipeline
            <>
              {' for'} <strong>{region ? region : 'ALL REGIONS'}</strong>{' '}
              {region ? 'region' : 'regions'}
            </>
            ?
          </AlertDialogDescription>
        }
        title="Provision Pipeline"
        onConfirm={handleOnConfirm}>
        <Button disabled={hasActivePipeline() || isLoading} loading={isLoading}>
          {team ? <TeamSpot showAbbreviation={false} team={team} /> : null}
          Provision {feedType} ({callSign})
        </Button>
      </ConfirmationDialog>
    </div>
  );
}
